/**
 * Check if a value is a mathematical expression or just plain text
 * @param {string} value - The input value to check
 * @returns {boolean} - True if it's an expression, false if it's plain text
 */
export const isExpression = value => {
  if (!value || typeof value !== 'string') return false;

  // Trim and check if empty
  const trimmed = value.trim();
  if (!trimmed) return false;

  // Check if contains at least one mathematical operator
  const hasOperator = /[+\-*/()]/.test(trimmed);

  if (!hasOperator) return false;

  // Validate that operators are in correct positions (not just random characters)
  // Allow: numbers, decimal points, operators, parentheses, and spaces
  const isValidExpression = /^[\d.+\-*/().\s]+$/.test(trimmed);

  return isValidExpression;
};

/**
 * Check if a value is a valid number (without operators)
 * @param {string} value - The input value to check
 * @returns {boolean} - True if it's a valid number
 */
export const isPlainNumber = value => {
  if (!value || typeof value !== 'string') return false;

  const trimmed = value.trim();
  if (!trimmed) return false;

  // Check if it's only digits and decimal point (no operators)
  const isNumber = /^[\d.]+$/.test(trimmed);

  return isNumber;
};

/**
 * Get the type of input value
 * @param {string} value - The input value to check
 * @returns {string} - 'expression', 'number', 'text', or 'invalid'
 */
export const getInputType = value => {
  if (!value || typeof value !== 'string') {
    if (typeof value === 'number') {
      return 'number';
    }
    if (typeof value === 'boolean') {
      return 'boolean';
    }
    if (typeof value === 'object' && Array.isArray(value)) {
      return 'array';
    }
    return 'invalid';
  }

  const trimmed = value.trim();
  if (!trimmed) return 'invalid';

  // Check if it's a number
  if (isPlainNumber(trimmed)) return 'number';

  // Check if it's an expression
  if (isExpression(trimmed)) return 'expression';

  // Otherwise it's plain text
  return 'text';
};

/**
 * Evaluate a mathematical expression and return the result
 * Supports: +, -, *, /, (), and decimals
 * @param {string} expression - The mathematical expression to evaluate
 * @returns {number|null} - The calculated result or null if invalid
 */
export const evaluateExpression = expression => {
  if (!expression || typeof expression !== 'string') return null;

  const trimmed = expression.trim();
  if (!trimmed) return null;

  try {
    // Sanitize: only allow numbers, operators, parentheses, and spaces
    const sanitized = trimmed.replace(/[^\d.+\-*/().\s]/g, '');

    // Validate sanitized expression is not empty
    if (!sanitized.trim()) return null;

    // // Check if sanitized expression matches original (no invalid characters removed)
    // if (sanitized !== trimmed) {
    //   console.warn('Expression contains invalid characters and was sanitized');
    // }

    // Evaluate the expression safely using Function constructor
    const result = Function('"use strict"; return (' + sanitized + ')')();

    // Validate result is a valid number
    if (typeof result !== 'number' || isNaN(result)) {
      return null;
    }

    return result;
  } catch {
    // console.error('Expression evaluation error:', error.message);
    return null;
  }
};

/**
 * Check if an expression is valid and has complete structure
 * A valid expression must have:
 * - At least one number
 * - At least one operator (if claimed to be expression)
 * - Balanced parentheses
 * - Correct operator placement (not consecutive, not at start/end)
 * @param {string} expression - The expression to validate
 * @returns {boolean} - True if valid and complete expression
 */
export const isValidExpression = expression => {
  if (!expression || typeof expression !== 'string') return false;

  const trimmed = expression.trim();
  if (!trimmed) return false;

  // 1. Must contain at least one number
  if (!/\d/.test(trimmed)) return false;

  // 2. Must contain at least one operator or parentheses
  if (!/[+\-*/()]/.test(trimmed)) return false;

  // 3. Only allow valid characters
  if (!/^[\d.+\-*/().\s]+$/.test(trimmed)) return false;

  // 4. Check balanced parentheses
  let parenCount = 0;
  for (const char of trimmed) {
    if (char === '(') parenCount++;
    if (char === ')') parenCount--;
    if (parenCount < 0) return false; // Closing before opening
  }
  if (parenCount !== 0) return false; // Unbalanced

  // 5. Check for invalid operator placement
  // Cannot start with operator (except - and +)
  if (/^[*/]/.test(trimmed.trim())) return false;

  // Cannot end with operator
  if (/[+\-*/]\s*$/.test(trimmed)) return false;

  // Cannot have consecutive operators (except negative numbers like -5 or 2*-5)
  if (/[+\-*/]\s*[*/]/.test(trimmed)) return false; // */  or /*
  if (/\*\s*[+-]/.test(trimmed)) return false; // *+ or *-

  // 6. Check invalid patterns
  // Cannot have empty parentheses
  if (/\(\s*\)/.test(trimmed)) return false;

  // Cannot have operators inside parentheses at start/end: (+ or -)
  if (/\(\s*[+\-*/]/.test(trimmed)) return false;
  if (/[+\-*/]\s*\)/.test(trimmed)) return false;

  // 7. Try to evaluate to confirm it's mathematically valid
  try {
    const sanitized = trimmed.replace(/[^\d.+\-*/().\s]/g, '');
    const result = Function('"use strict"; return (' + sanitized + ')')();

    // Must result in a valid number
    if (typeof result !== 'number' || isNaN(result)) return false;
  } catch {
    return false;
  }

  return true;
};
